import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, MapPin } from "lucide-react";

import { PARTY } from "../config/party";

const CHARS = "!<>-_\\/[]{}=+*^?#0137";
const SECRET = PARTY.locationLines;

export const LocationReveal = () => {
  const [decoded, setDecoded] = useState(false);
  const [rsvpd, setRsvpd] = useState(() => {
    try {
      return !!localStorage.getItem("rotu_rsvpd");
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    const on = () => setRsvpd(true);
    window.addEventListener("rotu:rsvpd", on);
    return () => window.removeEventListener("rotu:rsvpd", on);
  }, []);
  const [lines, setLines] = useState(["#### #### #### #### ####", "#### ######### #########", "#### #### #### #### ####", "#### ### #### #### ####"]);

  const decode = () => {
    if (decoded || !rsvpd) return;
    setDecoded(true);
    let frame = 0;
    const total = 40;
    const id = setInterval(() => {
      frame++;
      const progress = frame / total;
      setLines(
        SECRET.map((line) =>
          line
            .split("")
            .map((ch, i) => {
              if (ch === " ") return " ";
              const reveal = i / line.length < progress * 1.4 - 0.2;
              return reveal ? ch : CHARS[Math.floor(Math.random() * CHARS.length)];
            })
            .join("")
        )
      );
      if (frame >= total) {
        clearInterval(id);
        setLines(SECRET);
      }
    }, 45);
  };

  return (
    <section className="relative z-10 px-6 sm:px-12 lg:px-24 py-28" data-testid="location-section">
      <div className="max-w-3xl mx-auto border border-[rgba(0,240,255,0.25)] bg-[#111118]/70 backdrop-blur-xl p-8 sm:p-14 text-center relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1555086156-e6c7353d283f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="flex items-center justify-center gap-3 text-[11px] tracking-[0.5em] uppercase text-[#00F0FF] mb-6">
            <MapPin size={14} /> The location
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold uppercase text-white mb-10">
            {decoded ? "Signal decrypted" : "Signal encrypted"}
          </h2>

          <div className="font-mono text-sm sm:text-base text-[#39FF14] leading-loose mb-10 min-h-[7.5rem]" data-testid="decoded-coordinates">
            {lines.map((l, i) => (
              <div key={i} className={decoded ? "glitchy" : "text-zinc-600"}>
                {l}
              </div>
            ))}
          </div>

          <button
            onClick={decode}
            disabled={decoded || !rsvpd}
            data-testid="decode-button"
            className="group relative inline-flex items-center gap-3 px-8 py-4 border border-[#00F0FF] text-[#00F0FF] font-display text-xs font-bold uppercase tracking-widest overflow-hidden disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <span className="absolute inset-0 bg-[#00F0FF] translate-y-full group-hover:translate-y-0 group-disabled:translate-y-full transition-transform duration-300" />
            <Lock size={14} className="relative group-hover:text-[#08080C] transition-colors duration-300" />
            <span className="relative group-hover:text-[#08080C] transition-colors duration-300">
              {decoded ? "Decrypted" : rsvpd ? "Decode transmission" : "RSVP to decode"}
            </span>
          </button>
        </motion.div>
      </div>
    </section>
  );
};
